import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { OAuthService } from 'angular-oauth2-oidc';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { SicotService } from '../sicot.service';
import { Cot } from '../types/Cot';
import { TipoSegnalazioneAdmin } from '../types/TipoSegnalazioneAdmin';
import { CotAdminUser } from '../types/CotAdminUser';
import { CollegaTipoSegnalazioneDialogComponent } from '../collega-tipo-segnalazione-dialog/collega-tipo-segnalazione-dialog.component';

@Component({
  selector: 'app-mio-profilo',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    HeaderComponent,
    FooterComponent,
  ],
  templateUrl: './mio-profilo.component.html',
  styleUrl: './mio-profilo.component.css',
})
export class MioProfiloComponent implements OnInit {
  private readonly sicot = inject(SicotService);
  private readonly oauthService = inject(OAuthService);
  private readonly dialog = inject(MatDialog);

  username: string = 'Utente';
  cotAbilitate: Cot[] = [];
  tipiSegnalazione: TipoSegnalazioneAdmin[] = [];
  loading = true;
  loadingTipi = true;

  ngOnInit(): void {
    this.username = this.getUtente();
    const userId = this.sicot.getUserId();
    if (!userId) {
      this.cotAbilitate = [];
      this.tipiSegnalazione = [];
      this.loading = false;
      this.loadingTipi = false;
      return;
    }
    this.sicot.getUserCots(userId).subscribe({
      next: (list) => {
        this.cotAbilitate = list ?? [];
        this.loading = false;
      },
      error: () => {
        this.cotAbilitate = [];
        this.loading = false;
      },
    });
    this.sicot.getUserTipiSegnalazione(userId).subscribe({
      next: (list) => {
        this.tipiSegnalazione = [...(list ?? [])].sort((a, b) =>
          (a.descrizione ?? '').localeCompare(b.descrizione ?? '', 'it')
        );
        this.loadingTipi = false;
      },
      error: () => {
        this.tipiSegnalazione = [];
        this.loadingTipi = false;
      },
    });
  }

  /** Apre il dialog per gestire i tipi segnalazione dell'utente loggato (solo dalla propria area personale). */
  gestisciTipiSegnalazione(): void {
    const userId = this.sicot.getUserId();
    if (!userId) return;
    const currentUser: CotAdminUser = {
      id: userId,
      username: this.username,
      nome: '',
      cognome: '',
      email: '',
    };
    this.dialog
      .open(CollegaTipoSegnalazioneDialogComponent, {
        width: '480px',
        data: { user: currentUser },
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved === true) this.ricaricaTipiSegnalazione();
      });
  }

  private ricaricaTipiSegnalazione(): void {
    const userId = this.sicot.getUserId();
    if (!userId) return;
    this.loadingTipi = true;
    this.sicot.getUserTipiSegnalazione(userId).subscribe({
      next: (list) => {
        this.tipiSegnalazione = [...(list ?? [])].sort((a, b) =>
          (a.descrizione ?? '').localeCompare(b.descrizione ?? '', 'it')
        );
        this.loadingTipi = false;
      },
      error: () => {
        this.tipiSegnalazione = [];
        this.loadingTipi = false;
      },
    });
  }

  /** Stessa logica dell'header: username da SicotService, da OAuth claims, o "Utente". */
  private getUtente(): string {
    const fromSicot = this.sicot.getUsername();
    if (fromSicot) return fromSicot;
    const claims = this.oauthService.getIdentityClaims() as Record<string, unknown> | null;
    const fromClaims = claims?.['preferred_username'] ?? claims?.['name'] ?? claims?.['sub'];
    if (fromClaims && typeof fromClaims === 'string') return fromClaims;
    return 'Utente';
  }
}
